<template>
  <div
    ref="configurableSizes"
    class="configurable__container"
  >
    <div class="configurable__sizes">
      <div
        v-if="parentType !== 'GroupedProduct'"
        class="configurable__heading"
      >
        <div class="title">
          <span>{{ attribute.label }}</span>
        </div>
        <div
          v-if="hasConversionLabels"
          class="configurable__conversion"
        >
          <button
            class="configurable-conversion__option button--link"
            :class="{ 'is-active': !conversionView }"
            @click="conversionView = false"
          >
            {{ $t('Maat op label') }}
          </button>
          <button
            class="configurable-conversion__option button--link"
            :class="{ 'is-active': conversionView }"
            @click="conversionView = true"
          >
            {{ $t('BE maat') }}
          </button>
          <div class="configurable-conversion__indicator" />
        </div>
        <div class="advice-button">
          <ProductSizeGuide
            v-if="!isFasletVisible"
            class="product__size-guide"
            :size-guide-id="product?.sizeTableIdentifier"
          />
          <ThirdpartyFasletSizing
            v-if="product"
            :product="product"
            :es-product="esProduct"
            class="product__size-guide"
            @faslet-visible="isFasletVisible = $event"
          />
        </div>
      </div>
    </div>
    <div
      ref="options"
      class="configurable__options"
      :class="{ 'alt-view': !conversionView }"
    >
      <ul class="configurable__list">
        <ProductConfigurableAttributeOption
          v-for="option in attribute.values"
          :key="`${product.id}_${attribute.attribute_code}_${option.value_index}`"
          :product="product"
          :attribute="attribute"
          :option="option"
          :selected-options="selectedOptions"
          :show-conversion-labels="hasConversionLabels"
          :stock-status="getStockStatusForOption(option)"
          class="configurable__item"
          @selected="selected"
        />
      </ul>
    </div>
    <div
      v-if="sizeAdvice"
      class="configurable__size-advice"
    >
      <IconRuler class="icon--ruler" />
      <span>
        {{ $t(sizeAdvice) }}
      </span>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { MagentoConfigurableProduct, MagentoConfigurableProductOption, MagentoConfigurableProductOptionsValue } from '~layers/app/types/magento'

export interface Props {
  product: MagentoConfigurableProduct
  attribute: MagentoConfigurableProductOption
  selectedOptions?: Record<string, MagentoConfigurableProductOptionsValue>
  parentType?: string
}
const props = defineProps<Props>()

const emit = defineEmits<{
  selected: Record<string, MagentoConfigurableProductOptionsValue | null>
}>()

const esProduct = inject('esProduct', null)

const conversionView = ref(false)
const isFasletVisible = ref(false)

const selected = (option: MagentoConfigurableProductOptionsValue) => {
  emit('selected', { [props.attribute.attribute_code]: option }, option)
  toggleSizes()
}

const hasConversionLabels = computed(() => {
  if (props?.parentType === 'GroupedProduct') return false

  const variants = props.product?.variants
  if (!Array.isArray(variants)) return false

  return variants.some(({ product, attributes }) => {
    const label = product?.del_size_conversion_label?.trim()
    if (!label) return false

    const attr = attributes.find(
      ({ code }) => code === props.attribute.attribute_code,
    )

    return attr && label !== attr.label
  })
})

const sizeAdvice = computed(() => {
  const attributes = (props.product as any)?.attributeGroups?.[0]?.attributes
  if (!Array.isArray(attributes)) {
    return null
  }

  const advice = attributes.find(attr => attr.code === 'del_c_maatadvies')?.value
  return advice || null
})

const isMounted = ref(false)
const meta = inject('meta', ref())
const { loadAvelonStockData, AvelonStockData, pendingAvelonStock } = usePDP(meta)

const getStockStatusForOption = (option: MagentoConfigurableProductOptionsValue) => {
  if (!isMounted.value || pendingAvelonStock.value) {
    return null
  }

  const matchingVariant = props.product.variants.find(variant =>
    variant.attributes.some(
      attr =>
        attr.code === props.attribute.attribute_code
        && attr.value_index === option.value_index,
    ),
  )

  const variantSku = matchingVariant?.product?.sku

  if (!variantSku || !AvelonStockData.value?.getStock?.stockStatus) {
    return null
  }

  const stockStatus = AvelonStockData.value.getStock.stockStatus.find(
    stock => stock.sku === variantSku,
  )

  return stockStatus?.status || null
}

onMounted(() => {
  isMounted.value = true
  if (pendingAvelonStock) {
    loadAvelonStockData()
  }
})

// interaction
const sizesOpen = ref(false)
const options = ref(null)
const configurableSizes = ref(null)

const toggleSizes = () => {
  sizesOpen.value = !sizesOpen.value
}

onClickOutside(configurableSizes, () => {
  sizesOpen.value = false
})
</script>

<style>
  @import url("~layers/app/components/Product/ConfigurableAttribute.css");
</style>
